import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiSearch, FiEye, FiEdit, FiRefreshCw, FiTrash2, FiDownload, FiUsers, FiUser, FiX, FiChevronDown, FiChevronUp, FiCopy } from 'react-icons/fi';
import { b2bAgenciesAPI, usersAPI, settingsAPI } from '../../services/api';
import StatusBadge from '../../components/Common/StatusBadge';
import Modal from '../../components/Common/Modal';
import ColumnCustomizer, { useColumnPreferences } from '../../components/Common/ColumnCustomizer';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import {
  runTableFilters,
  hasActiveClientTableFilters,
  CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT
} from '../../utils/tableFilters';
import { toast } from 'react-toastify';

const LeadList = () => {
  const { t } = useTranslation();
  const { canPerformAction, user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    owner_id: '',
    passport_no: '',
    phone: '',
    email: '',
    city: '',
    country: ''
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [salesTeam, setSalesTeam] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sources, setSources] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [updatingStatusLeadId, setUpdatingStatusLeadId] = useState(null);
  const [assignOwnerModalOpen, setAssignOwnerModalOpen] = useState(false);
  const [bulkAssignOwnerId, setBulkAssignOwnerId] = useState('');
  const [bulkAssignSubmitting, setBulkAssignSubmitting] = useState(false);
  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false);
  const [bulkChangeStatusValue, setBulkChangeStatusValue] = useState('');
  const [bulkChangeStatusSubmitting, setBulkChangeStatusSubmitting] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = String(user?.role || '').toLowerCase();
  const isSuperAdminOnly = role === 'super_admin';
  const isAdminOrSuperAdmin = role === 'super_admin' || role === 'admin';
  const copyToClipboard = useCallback(async (text, kind) => {
    const value = String(text || '').trim();
    if (!value || value === '-') return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(kind === 'phone' ? 'Phone number copied' : 'Email copied');
    } catch (_) {
      toast.error('Copy failed');
    }
  }, []);

  const leadColumns = useMemo(() => [
    { key: 'checkbox', label: '', locked: true },
    { key: 'ref_number', label: t('leads.tableRef') },
    { key: 'student_name', label: t('leads.tableAgencyName') },
    { key: 'contact_number', label: t('leads.tableContact') },
    { key: 'email', label: t('leads.tableEmail') },
    { key: 'source', label: t('leads.tableSource') },
    { key: 'status', label: t('leads.tableStatus') },
    { key: 'owner_name', label: t('leads.tableOwner') },
    { key: 'created_at', label: t('leads.tableCreated') },
    { key: 'decision_maker', label: t('leadDetail.contactPerson', 'Contact Person'), defaultVisible: false },
    { key: 'country', label: t('common.country'), defaultVisible: false },
    { key: 'state', label: t('common.state', 'State'), defaultVisible: false },
    { key: 'city', label: t('common.city'), defaultVisible: false },
    { key: 'closed_by_name', label: t('leadDetail.closedBy'), forceVisibleOnMigration: true },
    { key: 'remarks', label: t('common.remarks'), defaultVisible: false },
    { key: 'actions', label: t('common.actions'), locked: true }
  ], [t]);
  const { isVisible, toggleColumn, resetToDefault, columns: colDefs } = useColumnPreferences('leads', leadColumns);
  useEffect(() => {
    const passport = searchParams.get('passport_no');
    const phone = searchParams.get('phone');
    const email = searchParams.get('email');
    if (!passport && !phone && !email) return;
    setFilters((f) => ({
      ...f,
      ...(passport != null ? { passport_no: passport } : {}),
      ...(phone != null ? { phone: phone } : {}),
      ...(email != null ? { email: email } : {})
    }));
    setPage(1);
    setAdvancedOpen(true);
  }, [searchParams]);

  const clearAllFilters = () => {
    setSearch('');
    setFilters({
      status: '',
      source: '',
      owner_id: '',
      passport_no: '',
      phone: '',
      email: '',
      city: '',
      country: ''
    });
    setPage(1);
    setAdvancedOpen(false);
  };

  const exactFilterCount = [filters.passport_no, filters.phone, filters.email, filters.city, filters.country]
    .filter((v) => v && String(v).trim()).length;

  const sel = { fontSize: 13, padding: '6px 10px', minHeight: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--white)', color: 'var(--text)', maxWidth: '100%' };
  const inp = { fontSize: 13, padding: '6px 10px', height: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--white)', color: 'var(--text)', width: '100%', boxSizing: 'border-box' };

  const activeB2BStatuses = leadStatuses
    .filter(s => s.lead_group === 'B2B' && s.status === 'active')
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const b2bStatusOptions = activeB2BStatuses.map(s => s.status_name);
  /** Bulk change: B2B active statuses from Settings (Super Admin), except system-driven Tied-Up */
  const bulkManualStatusOptions = useMemo(
    () =>
      leadStatuses
        .filter((s) => s.lead_group === 'B2B' && s.status === 'active')
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map((s) => s.status_name)
        .filter((name) => name && name !== 'Tied-Up'),
    [leadStatuses]
  );

  const sourceOptions = sources.map(s => s.source_name).filter(Boolean);
  const columnFilterDefs = useMemo(() => {
    const ownerOptions = Array.from(new Set((agencies || []).map((l) => String(l.owner_name || '')).filter(Boolean)));
    const sourceOpts = Array.from(new Set((agencies || []).map((l) => String(l.source || '')).filter(Boolean)));
    return [
      { key: 'ref_number', label: t('leads.tableRef'), field: 'ref_number', type: 'text' },
      { key: 'student_name', label: t('leads.tableAgencyName'), field: 'student_name', type: 'text' },
      { key: 'contact_number', label: t('leads.tableContact'), field: 'contact_number', type: 'text' },
      { key: 'email', label: t('leads.tableEmail'), field: 'email', type: 'text' },
      { key: 'source', label: t('leads.tableSource'), field: 'source', type: 'multi', options: sourceOpts.map((v) => ({ value: v, label: v })) },
      { key: 'status', label: t('leads.tableStatus'), field: 'status', type: 'multi', options: b2bStatusOptions.map((v) => ({ value: v, label: v })) },
      { key: 'owner_name', label: t('leads.tableOwner'), field: 'owner_name', type: 'multi', options: ownerOptions.map((v) => ({ value: v, label: v })) },
      { key: 'created_at', label: t('leads.tableCreated'), field: 'created_at', type: 'date' },
      { key: 'decision_maker', label: t('leadDetail.contactPerson', 'Contact Person'), field: 'decision_maker', type: 'text' },
      { key: 'country', label: t('common.country'), field: 'country', type: 'text' },
      { key: 'state', label: t('common.state', 'State'), field: 'state', type: 'text' },
      { key: 'city', label: t('common.city'), field: 'city', type: 'text' },
      { key: 'closed_by_name', label: t('leadDetail.closedBy'), field: 'closed_by_name', type: 'text' },
      { key: 'remarks', label: t('common.remarks'), field: 'remarks', type: 'text' }
    ];
  }, [agencies, b2bStatusOptions, t]);
  const columnFilterMap = useMemo(() => Object.fromEntries(columnFilterDefs.map((d) => [d.key, d])), [columnFilterDefs]);
  const columnFilterDefsRef = useLatest(columnFilterDefs);
  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const filteredAgencies = useMemo(
    () => runTableFilters(agencies, tableFilters, tableFilters, [], columnFilterDefs),
    [agencies, tableFilters, columnFilterDefs]
  );
  const clientTableFilterMode = useMemo(
    () => hasActiveClientTableFilters(tableFilters, columnFilterDefs),
    [tableFilters, columnFilterDefs]
  );
  const pagedFilteredAgencies = useMemo(() => {
    if (!clientTableFilterMode) return filteredAgencies;
    const start = (page - 1) * limit;
    return filteredAgencies.slice(start, start + limit);
  }, [clientTableFilterMode, filteredAgencies, page, limit]);

  useEffect(() => {
    const len = clientTableFilterMode ? filteredAgencies.length : total;
    const tp = Math.max(1, Math.ceil(len / limit));
    if (page > tp) setPage(tp);
  }, [clientTableFilterMode, filteredAgencies.length, total, limit, page]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const active = clientTableFilterMode;
      const res = await b2bAgenciesAPI.getAll({
        page: active ? 1 : page,
        limit: active ? CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT : limit,
        search, sort_by: sortBy, sort_order: sortOrder,
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
      });
      setAgencies(res.data.agencies || res.data.leads || []);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Load leads error:', err);
    }
    setLoading(false);
  }, [page, limit, search, sortBy, sortOrder, filters, clientTableFilterMode]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  useEffect(() => {
    const statusParam = searchParams.get('status') || '';
    if (statusParam) {
      setFilters((f) => ({ ...f, status: statusParam }));
      setPage(1);
    }
  }, [searchParams]);

  useEffect(() => {
    usersAPI.getSalesTeam().then(res => setSalesTeam(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    settingsAPI.getSources().then(res => setSources(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    settingsAPI.getLeadStatuses().then(res => setLeadStatuses(res.data || [])).catch(() => {});
  }, []);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('leads.deleteConfirm'))) return;
    try {
      await b2bAgenciesAPI.delete(id);
      loadLeads();
    } catch (err) {
      alert(err.response?.data?.message || t('leads.deleteFailed'));
    }
  };

  // "Tied-Up" is system-driven via agreement upload, so keep it non-selectable manually.
  const getStatusOptionsForLead = (lead) => {
    const manualOptions = b2bStatusOptions.filter((s) => s !== 'Tied-Up');
    return lead?.status === 'Tied-Up' ? ['Tied-Up', ...manualOptions] : manualOptions;
  };

  const getInlineStatusStyle = (lead) => {
    const c = getB2BStatusColor(lead.status);
    return {
      background: c.bg,
      color: c.color,
      border: `1px solid ${c.color}33`
    };
  };

  const handleInlineStatusChange = async (lead, nextStatus) => {
    if (lead?.status === 'Tied-Up' && !isAdminOrSuperAdmin) return;
    if (!nextStatus || nextStatus === lead.status) return;
    setUpdatingStatusLeadId(lead.lead_id);
    try {
      await b2bAgenciesAPI.update(lead.lead_id, { status: nextStatus });
      setAgencies(prev => prev.map(l => (l.lead_id === lead.lead_id ? { ...l, status: nextStatus } : l)));
    } catch (err) {
      alert(err.response?.data?.message || t('leads.statusUpdateFailed'));
    } finally {
      setUpdatingStatusLeadId(null);
    }
  };

  const openBulkAssignOwnerModal = () => {
    if (selected.length === 0) {
      alert(t('leads.noLeadsSelected'));
      return;
    }
    setBulkAssignOwnerId('');
    setAssignOwnerModalOpen(true);
  };

  const closeBulkAssignOwnerModal = () => {
    if (bulkAssignSubmitting) return;
    setAssignOwnerModalOpen(false);
    setBulkAssignOwnerId('');
  };

  const applyBulkAssignOwner = async () => {
    const ownerId = parseInt(String(bulkAssignOwnerId), 10);
    if (!ownerId || Number.isNaN(ownerId)) {
      alert(t('leads.selectOwner'));
      return;
    }
    const owner = salesTeam.find((u) => String(u.user_id) === String(bulkAssignOwnerId));
    const ownerLabel = owner?.name || `User #${ownerId}`;
    const n = selected.length;
    const confirmMsg = t('leads.bulkAssignConfirm', { count: n, owner: ownerLabel });
    if (!window.confirm(confirmMsg)) return;

    try {
      setBulkAssignSubmitting(true);
      const res = await b2bAgenciesAPI.bulkAction({
        agency_ids: selected,
        action: 'assign_owner',
        value: ownerId
      });
      setAssignOwnerModalOpen(false);
      setBulkAssignOwnerId('');
      setSelected([]);
      loadLeads();
      alert(res.data?.message || t('leads.ownerAssigned'));
    } catch (err) {
      alert(err.response?.data?.message || t('leads.actionFailed'));
    } finally {
      setBulkAssignSubmitting(false);
    }
  };

  const openBulkChangeStatusModal = () => {
    if (selected.length === 0) {
      alert(t('leads.noLeadsSelected'));
      return;
    }
    setBulkChangeStatusValue('');
    setChangeStatusModalOpen(true);
  };

  const closeBulkChangeStatusModal = () => {
    if (bulkChangeStatusSubmitting) return;
    setChangeStatusModalOpen(false);
    setBulkChangeStatusValue('');
  };

  const applyBulkChangeStatus = async () => {
    const status = String(bulkChangeStatusValue || '').trim();
    if (!status) {
      alert(t('leads.selectStatus'));
      return;
    }
    if (!bulkManualStatusOptions.includes(status)) {
      alert(t('leads.invalidStatus'));
      return;
    }
    const n = selected.length;
    if (!window.confirm(t('leads.bulkStatusConfirm', { status, count: n }))) return;

    try {
      setBulkChangeStatusSubmitting(true);
      await b2bAgenciesAPI.bulkAction({
        agency_ids: selected,
        action: 'change_status',
        value: status
      });
      setChangeStatusModalOpen(false);
      setBulkChangeStatusValue('');
      setSelected([]);
      loadLeads();
    } catch (err) {
      alert(err.response?.data?.message || t('leads.actionFailed'));
    } finally {
      setBulkChangeStatusSubmitting(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.length === 0) {
      alert(t('leads.noLeadsSelected'));
      return;
    }
    if (action === 'delete') {
      const n = selected.length;
      if (!window.confirm(t('leads.bulkDeleteConfirm', { count: n }))) {
        return;
      }
    }
    try {
      await b2bAgenciesAPI.bulkAction({ agency_ids: selected, action, value: undefined });
      setSelected([]);
      loadLeads();
    } catch (err) {
      alert(err.response?.data?.message || t('leads.actionFailed'));
    }
  };

  const handleExport = () => {
    const headers = [t('leads.tableRef'), t('leads.tableAgencyName'), t('leads.tableContact'), t('leads.tableEmail'), t('leads.tableSource'), t('leads.tableStatus'), t('leads.tableOwner'), t('leads.tableCreated')];
    const rows = agencies.map(l => [l.ref_number, l.student_name, l.contact_number, l.email, l.source, l.status, l.owner_name || '', l.created_at]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v || ''}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'b2b_agencies_export.csv'; a.click();
  };

  const listTotalForPaging = clientTableFilterMode ? filteredAgencies.length : total;
  const totalPages = Math.max(1, Math.ceil(listTotalForPaging / limit));
  const quickFilterStatuses = ['', ...b2bStatusOptions];

  // B2B status badge color helper
  const getB2BStatusColor = (status) => {
    const colors = {
      'New': { bg: 'var(--info-light)', color: 'var(--info)' },
      'Contacted': { bg: '#E0E7FF', color: '#4338CA' },
      'Proposal Sent': { bg: '#FEF3C7', color: '#D97706' },
      'Negotiation': { bg: '#DBEAFE', color: '#2563EB' },
      'Tied-Up': { bg: '#D1FAE5', color: '#059669' },
      'Agreement Done': { bg: '#ECFDF5', color: '#047857' },
      'Not Interested': { bg: '#FEE2E2', color: '#DC2626' },
      'Closed': { bg: '#F3F4F6', color: '#6B7280' }
    };
    return colors[status] || { bg: '#F3F4F6', color: '#6B7280' };
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t('leads.title')}</h1>
        <div className="page-actions">
          {isSuperAdminOnly && (
            <button className="btn btn-outline btn-sm" onClick={handleExport}><FiDownload /> {t('common.exportCsv')}</button>
          )}
          {canPerformAction('b2b_agencies', 'create') && (
            <button className="btn btn-primary" onClick={() => navigate('/b2b-agencies/new')}><FiPlus /> {t('leads.addAgency')}</button>
          )}
        </div>
      </div>

      {/* Quick Filters */}
      <div className="quick-filters">
        {quickFilterStatuses.map(s => (
          <button key={s} className={filters.status === s ? 'active' : ''}
            onClick={() => { setFilters(f => ({ ...f, status: s })); setPage(1); }}>
            {s || t('common.all')}
          </button>
        ))}
      </div>

      <div className="table-container">
        <div
          style={{
            padding: '10px 12px',
            background: 'var(--bg-secondary, #f8fafc)',
            borderBottom: '1px solid var(--border)'
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <div className="table-search" style={{ flex: '1 1 200px', minWidth: 180, maxWidth: 400, margin: 0 }}>
              <FiSearch className="search-icon" />
              <input
                placeholder={t('leads.searchPlaceholder')}
                value={tableFilters.globalSearch}
                onChange={(e) => { setTableFilters((prev) => ({ ...prev, globalSearch: e.target.value })); setPage(1); }}
                style={{ fontSize: 13 }}
              />
            </div>
            <select style={{ ...sel, minWidth: 110 }} value={filters.source} onChange={(e) => { setFilters((f) => ({ ...f, source: e.target.value })); setPage(1); }} title={t('leads.sourceFilter')}>
              <option value="">{t('leads.sourceFilter')}</option>
              {sourceOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select style={{ ...sel, minWidth: 120 }} value={filters.owner_id} onChange={(e) => { setFilters((f) => ({ ...f, owner_id: e.target.value })); setPage(1); }} title={t('leads.ownerFilter')}>
              <option value="">{t('leads.ownerFilter')}</option>
              {salesTeam.map((u) => (
                <option key={u.user_id} value={u.user_id}>{u.name}</option>
              ))}
            </select>
            <button
              type="button"
              className={`btn btn-sm ${advancedOpen || exactFilterCount > 0 ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setAdvancedOpen((o) => !o)}
              title={t('leads.passportPhoneEmailCityCountry')}
              style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {advancedOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              {t('leads.exact')}
              {exactFilterCount > 0 ? <span style={{ opacity: 0.9, fontSize: 11 }}>({exactFilterCount})</span> : null}
            </button>
            <button type="button" className="btn btn-sm btn-outline" onClick={clearAllFilters} title={t('leads.resetAll')}>
              <FiX size={16} />
            </button>
            <ColumnCustomizer columns={colDefs} isVisible={isVisible} toggleColumn={toggleColumn} resetToDefault={resetToDefault} />
            <div style={{ flex: '1 1 12px', minWidth: 0 }} aria-hidden />
            {selected.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#666' }}>{selected.length} {t('common.selected')}</span>
                <button type="button" className="btn btn-sm btn-outline" onClick={openBulkAssignOwnerModal}>
                  {t('leads.assignOwner')}
                </button>
                <button type="button" className="btn btn-sm btn-outline" onClick={openBulkChangeStatusModal}>
                  {t('leads.changeStatus')}
                </button>
                <button type="button" className="btn btn-sm btn-danger" onClick={() => handleBulkAction('delete')}>
                  {t('common.delete')}
                </button>
              </div>
            )}
          </div>
          {advancedOpen && (
            <div
              style={{
                marginTop: 10,
                padding: '10px 10px 8px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--white)',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '8px 12px'
              }}
            >
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('leads.passportLabel')}</label>
                <input type="text" placeholder={t('leads.passportPlaceholder')} value={filters.passport_no} style={inp} autoComplete="off"
                  onChange={(e) => { setFilters((f) => ({ ...f, passport_no: e.target.value })); setPage(1); }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('leads.phoneLabel')}</label>
                <input type="text" placeholder={t('leads.phonePlaceholder')} value={filters.phone} style={inp} autoComplete="off"
                  onChange={(e) => { setFilters((f) => ({ ...f, phone: e.target.value })); setPage(1); }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('leads.emailLabel')}</label>
                <input type="text" placeholder={t('leads.emailPlaceholder')} value={filters.email} style={inp} autoComplete="off"
                  onChange={(e) => { setFilters((f) => ({ ...f, email: e.target.value })); setPage(1); }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('leads.cityLabel')}</label>
                <input type="text" placeholder={t('common.city')} value={filters.city} style={inp} autoComplete="off"
                  onChange={(e) => { setFilters((f) => ({ ...f, city: e.target.value })); setPage(1); }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('leads.countryLabel')}</label>
                <input type="text" placeholder={t('common.country')} value={filters.country} style={inp} autoComplete="off"
                  onChange={(e) => { setFilters((f) => ({ ...f, country: e.target.value })); setPage(1); }} />
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner"></div></div>
        ) : (
          <>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input type="checkbox" checked={selected.length === agencies.length && agencies.length > 0}
                        onChange={(e) => setSelected(e.target.checked ? agencies.map(l => l.lead_id) : [])} />
                    </th>
                    {isVisible('ref_number') && <UnifiedFilterHeaderCell column={columnFilterMap.ref_number} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('student_name') && <UnifiedFilterHeaderCell column={columnFilterMap.student_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('contact_number') && <UnifiedFilterHeaderCell column={columnFilterMap.contact_number} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('email') && <UnifiedFilterHeaderCell column={columnFilterMap.email} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('source') && <UnifiedFilterHeaderCell column={columnFilterMap.source} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('status') && <UnifiedFilterHeaderCell column={columnFilterMap.status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('owner_name') && <UnifiedFilterHeaderCell column={columnFilterMap.owner_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('created_at') && <UnifiedFilterHeaderCell column={columnFilterMap.created_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('decision_maker') && <UnifiedFilterHeaderCell column={columnFilterMap.decision_maker} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('country') && <UnifiedFilterHeaderCell column={columnFilterMap.country} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('state') && <UnifiedFilterHeaderCell column={columnFilterMap.state} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('city') && <UnifiedFilterHeaderCell column={columnFilterMap.city} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('closed_by_name') && <UnifiedFilterHeaderCell column={columnFilterMap.closed_by_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('remarks') && <UnifiedFilterHeaderCell column={columnFilterMap.remarks} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFilteredAgencies.map(lead => (
                    <tr key={lead.lead_id}>
                      <td>
                        <input type="checkbox" checked={selected.includes(lead.lead_id)}
                          onChange={(e) => setSelected(e.target.checked ? [...selected, lead.lead_id] : selected.filter(id => id !== lead.lead_id))} />
                      </td>
                      {isVisible('ref_number') && <td style={{ fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate(`/b2b-agencies/${lead.lead_id}`)} title={t('leads.openDetails')}>{lead.ref_number}</td>}
                      {isVisible('student_name') && <td style={{ fontWeight: 500 }}>{lead.student_name}</td>}
                      {isVisible('contact_number') && (
                        <td style={{ fontSize: 13 }}>
                          {lead.contact_number ? (
                            <button
                              type="button"
                              onClick={() => copyToClipboard(`${lead.phone_country_code || ''} ${lead.contact_number || ''}`.trim(), 'phone')}
                              style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                              title="Copy phone number"
                            >
                              <span>{`${lead.phone_country_code || ''} ${lead.contact_number || ''}`.trim()}</span>
                              <FiCopy size={13} />
                            </button>
                          ) : '-'}
                        </td>
                      )}
                      {isVisible('email') && (
                        <td style={{ fontSize: 13 }}>
                          {lead.email ? (
                            <button
                              type="button"
                              onClick={() => copyToClipboard(lead.email, 'email')}
                              style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                              title="Copy email"
                            >
                              <span>{lead.email}</span>
                              <FiCopy size={13} />
                            </button>
                          ) : '-'}
                        </td>
                      )}
                      {isVisible('source') && <td style={{ fontSize: 13 }}>{lead.source}</td>}
                      {isVisible('status') && <td>
                        {(() => {
                          const isB2BTiedUpLocked = lead?.status === 'Tied-Up' && !isAdminOrSuperAdmin;
                          const isDisabled = updatingStatusLeadId === lead.lead_id || isB2BTiedUpLocked;
                          return (
                        <select
                          value={lead.status || ''}
                          disabled={isDisabled}
                          onChange={(e) => handleInlineStatusChange(lead, e.target.value)}
                          style={{
                            minWidth: 165, height: 34, padding: '0 10px', borderRadius: 10,
                            fontSize: 12, fontWeight: 600, outline: 'none',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.65 : 1,
                            transition: 'all .15s ease',
                            ...getInlineStatusStyle(lead)
                          }}
                          title={isB2BTiedUpLocked ? t('leads.statusLocked') : t('leads.updateStatus')}
                        >
                          {getStatusOptionsForLead(lead).map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                          );
                        })()}
                      </td>}
                      {isVisible('owner_name') && <td style={{ fontSize: 13 }}>{lead.owner_name || '-'}</td>}
                      {isVisible('created_at') && <td style={{ fontSize: 13 }}>{new Date(lead.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>}
                      {isVisible('decision_maker') && <td style={{ fontSize: 13 }}>{lead.decision_maker || '-'}</td>}
                      {isVisible('country') && <td style={{ fontSize: 13 }}>{lead.country || '-'}</td>}
                      {isVisible('state') && <td style={{ fontSize: 13 }}>{lead.state || '-'}</td>}
                      {isVisible('city') && <td style={{ fontSize: 13 }}>{lead.city || '-'}</td>}
                      {isVisible('closed_by_name') && <td style={{ fontSize: 13 }}>{lead.closed_by_name || '-'}</td>}
                      {isVisible('remarks') && <td style={{ fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.remarks || '-'}</td>}
                      <td>
                        <div className="table-actions">
                          <button className="view" title="View" onClick={() => navigate(`/b2b-agencies/${lead.lead_id}`)}><FiEye /></button>
                          <button className="edit" title="Edit" onClick={() => navigate(`/b2b-agencies/${lead.lead_id}/edit`)}><FiEdit /></button>
                          {canPerformAction('b2b_agencies', 'delete') && lead.status !== 'Agreement Done' && (
                            <button className="delete" title="Delete" onClick={() => handleDelete(lead.lead_id)}><FiTrash2 /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredAgencies.length === 0 && (
                    <tr>
                      <td colSpan={100} className="text-center" style={{ padding: 40, color: '#999' }}>
                        {t('leads.noAgencies')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-pagination">
              <span>{t('leads.showingAgencies', { start: listTotalForPaging > 0 ? ((page - 1) * limit) + 1 : 0, end: Math.min(page * limit, listTotalForPaging), total: listTotalForPaging })}</span>
              <div className="pagination-btns">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{t('common.prev')}</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  return p <= totalPages ? (
                    <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                  ) : null;
                })}
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('common.next')}</button>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={assignOwnerModalOpen}
        onClose={closeBulkAssignOwnerModal}
        title={t('leads.bulkAssignTitle')}
        footer={
          <>
            <button type="button" className="btn btn-outline" disabled={bulkAssignSubmitting} onClick={closeBulkAssignOwnerModal}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary" disabled={bulkAssignSubmitting} onClick={applyBulkAssignOwner}>
              {bulkAssignSubmitting ? t('common.applying') : t('common.apply')}
            </button>
          </>
        }
      >
        <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--text-secondary)' }}>
          {t('leads.bulkAssignBody', { count: selected.length })}
        </p>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>{t('common.owner')}</label>
          <select
            className="form-control"
            style={{ width: '100%', maxWidth: '100%', padding: '10px 12px', borderRadius: 8 }}
            value={bulkAssignOwnerId}
            onChange={(e) => setBulkAssignOwnerId(e.target.value)}
          >
            <option value="">{t('leads.selectOwnerDefault')}</option>
            {salesTeam.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.name}
                {u.email ? ` (${u.email})` : ''}
              </option>
            ))}
          </select>
        </div>
      </Modal>

      <Modal
        isOpen={changeStatusModalOpen}
        onClose={closeBulkChangeStatusModal}
        title={t('leads.bulkStatusTitle')}
        footer={
          <>
            <button type="button" className="btn btn-outline" disabled={bulkChangeStatusSubmitting} onClick={closeBulkChangeStatusModal}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={bulkChangeStatusSubmitting || bulkManualStatusOptions.length === 0}
              onClick={applyBulkChangeStatus}
            >
              {bulkChangeStatusSubmitting ? t('common.applying') : t('common.apply')}
            </button>
          </>
        }
      >
        <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--text-secondary)' }}>
          {t('leads.bulkStatusBody', { count: selected.length })}
        </p>
        {bulkManualStatusOptions.length === 0 ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--warning)' }}>
            {t('leads.noEligibleStatuses')}
          </p>
        ) : (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>{t('leads.newStatusLabel')}</label>
            <select
              className="form-control"
              style={{ width: '100%', maxWidth: '100%', padding: '10px 12px', borderRadius: 8 }}
              value={bulkChangeStatusValue}
              onChange={(e) => setBulkChangeStatusValue(e.target.value)}
            >
              <option value="">{t('leads.selectStatusDefault')}</option>
              {bulkManualStatusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LeadList;
